import type { EntityOrigin, GeometryLabelDto, UserDto } from '@/api/generator';
import { translation } from '@/locales/translation';
import { t } from 'i18next';
import { get, has, isString } from 'lodash';
import type { Paths } from 'type-fest';
import type { I18nKey } from './typings';

export function makeCopyText(text?: string | null) {
  return `${text} (${t('DuplicateSuffix')})`;
}

export type Nullable<T> = { [K in keyof T]: T[K] | null };

export function objectFromKeys<K extends string | number | symbol, T>(
  keys: Readonly<Array<K>>,
  iteratee?: (key: K) => T,
): Record<K, T> {
  return keys.reduce<Record<K, T>>(
    (obj, key) => ({
      ...obj,
      [key]: iteratee?.(key),
    }),
    {} as Record<K, T>,
  );
}

export function getTranslatedOptions<T extends string>(keys: T[], context: string) {
  return keys
    .map((key) => {
      return {
        title: t((context ? `${context}.${key}` : key) as I18nKey),
        value: key,
      };
    })
    .sort((a, b) => {
      return a.title.localeCompare(b.title, undefined, {
        sensitivity: 'base',
      });
    });
}

export interface TranslatableEntity {
  name?: string | null;
  iuid?: string | null;
  origin?: EntityOrigin | null;
  geometry?: GeometryLabelDto | null;
  description?: string | null;
}

/**
 * Checks if the given key has a corresponding translation entry
 */
export function hasTranslationKey(key?: string | null) {
  if (!key) return false;
  return has(translation, key) && typeof get(translation, key) === 'string';
}

/**
 * Determines whether the given entity is of origin "Predefined"
 *
 * @param entity The entity to check
 * @returns True if the entity should be translated, false otherwise
 */
export function isPredefinedEntity(entity?: TranslatableEntity | null) {
  return entity?.origin === 'Predefined';
}

/**
 * Translates the specified property of a TranslatableEntity, if the entity should be translated.
 *
 * @param propertyPath - default: 'name' - The property path to translate.
 * @param entity - The TranslatableEntity to translate.
 * @returns The translated property value, or the original value if the entity should not be translated.
 */
export function translateEntityProperty<T extends TranslatableEntity>(
  propertyPath: NonNullable<Paths<T>>,
  entity?: T | null,
) {
  const value = get(entity, propertyPath);
  if (!value || !isString(value)) return '';

  if (isPredefinedEntity(entity) || hasTranslationKey(value)) {
    // Log warning for missing translation keys if not in production mode
    if (!hasTranslationKey(value)) {
      console.warn(`Missing translation key: ${value}`);
    }

    return t(value as I18nKey);
  }

  return value;
}

/**
 * Checks if the translated name of the entity includes the given search string
 * @param entity
 * @param searchString
 */
export function translatedEntityNameIncludes(
  entity?: TranslatableEntity | null,
  searchString?: string,
) {
  if (!searchString) return true;
  const search = searchString.toLowerCase();
  return (
    translateEntityProperty('name', entity)?.toLowerCase().includes(search) ||
    entity?.geometry?.name?.toLowerCase().includes(search) ||
    false
  );
}

type UserFullName = Pick<Partial<UserDto>, 'title' | 'firstName' | 'middleName' | 'lastName'>;

export function fullName(user?: UserFullName) {
  return user
    ? [user.title, user.firstName, user.middleName, user.lastName].filter(Boolean).join(' ')
    : undefined;
}

export function fullNameWithIuid(user: UserFullName & Pick<UserDto, 'iuid'>) {
  return `${fullName(user)} | ${user.iuid}`;
}

type Truthy<T> = T extends false | '' | 0 | null | undefined ? never : T;

export function truthy<T>(value: T): value is Truthy<T> {
  return !!value;
}

export function awaitTimeout(delay: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, delay));
}
